import React, { ReactNode } from 'react';
import { css, ThemeProvider, useTheme } from 'styled-components/macro';
import { GlobalStyles } from './global-styles';

// ===== Colors =====
export const DSLightColors = {
  black: '#000000',
  white: '#FFFFFF',
  gray: {
    50: '#F9FAFC',
    100: '#F2F5F8',
    200: '#E4E8EE',
    300: '#CED5DE',
    400: '#98A3B4',
    500: '#687386',
    600: '#495566',
    700: '#354153',
    800: '#1F2939',
    900: '#0F1728',
  },
  primary: {
    50: '#EEF5FE',
    100: '#E0EEFF',
    200: '#BEDAFD',
    300: '#93C4FC',
    400: '#60A5F9',
    500: '#3B81F4',
    600: '#2D67D8',
    700: '#1D4ED6',
    800: '#1E40AD',
    900: '#1E3A8A',
  },
  orange: {
    50: '#FEFAEA',
    100: '#FDF2C6',
    200: '#FCE58A',
    300: '#FBD24D',
    400: '#FABE24',
    500: '#F49D0B',
    600: '#D87706',
    700: '#B35309',
    800: '#91400E',
    900: '#76340F',
  },
  red: {
    50: '#FFF5F5',
    100: '#FEE3E3',
    200: '#FED7D7',
    300: '#FEB2B2',
    400: '#FC8181',
    500: '#F56565',
    600: '#E53E3E',
    700: '#C53030',
    800: '#9B2C2C',
    900: '#822727',
  },
  green: {
    50: '#EFFCF3',
    100: '#DBFBE6',
    200: '#BAF6CF',
    300: '#86EEAB',
    400: '#4ADC7F',
    500: '#22C35D',
    600: '#16A24A',
    700: '#157F3D',
    800: '#166434',
    900: '#14522D',
  },
  purple: {
    50: '#F9F4FE',
    100: '#F2E7FE',
    200: '#E8D4FE',
    300: '#D7B3FD',
    400: '#BE83FA',
    500: '#A754F5',
    600: '#9233E8',
    700: '#7D22CC',
    800: '#6921A5',
    900: '#561C84',
  },
} as const;

// ===== Spacings =====
export const DS_SPACINGS = {
  xxs: '4px',
  xs: '8px',
  s: '12px',
  componentOnly: '10px',
  base: '16px',
  m: '24px',
  l: '32px',
  xl: '48px',
  xxl: '64px',
  xxxl: '128px',
} as const;

// ===== Radiuses =====
export const DS_RADIUSES = {
  xxxs: '4px',
  xxs: '6px',
  xs: '8px',
  s: '12px',
  base: '16px',
  m: '24px',
  l: '32px',
  full: '999px',
} as const;

// ===== Shadows =====
export const DS_SHADOWS = {
  xs: '0 1px 2px rgba(15, 23, 40, 0.06)',
  sm: '0 0 8px 4px rgba(15, 23, 40, 0.04), 0 1px 3px rgba(15, 23, 40, 0.12), 0 1px 2px rgba(15, 23, 40, 0.04)',
  md: '0 0 10px 6px rgba(15, 23, 40, 0.04), 0 4px 8px rgba(15, 23, 40, 0.12), 0 2px 4px rgba(15, 23, 40, 0.04)',
  lg: '0 0 10px 6px rgba(15, 23, 40, 0.04), 0 12px 18px rgba(15, 23, 40, 0.12), 0 4px 8px rgba(15, 23, 40, 0.04)',
  xl: '0 0 10px 6px rgba(15, 23, 40, 0.04), 0 18px 22px rgba(15, 23, 40, 0.08), 0 8px 10px rgba(15, 23, 40, 0.04)',
  '2xl': '0 24px 46px -10px rgba(15, 23, 40, 0.16)',
} as const;

// ===== Tokens =====
const DS_BREAKPOINTS = {
  mobile: 480,
  tablet: 834,
  desktop: 1024,
} as const;

export const DS_TOKENS = {
  modal: {
    backdrop: {
      bgColor: '#000000',
      opacity: '0.8',
    },
  },
  media: {
    mobile: `@media (max-width: ${DS_BREAKPOINTS.tablet - 1}px)`,
    tablet: `@media (min-width: ${DS_BREAKPOINTS.tablet}px) and (max-width: ${
      DS_BREAKPOINTS.desktop - 1
    }px)`,
    desktop: `@media (min-width: ${DS_BREAKPOINTS.desktop}px)`,
    up(bp: keyof typeof DS_BREAKPOINTS) {
      return `@media (min-width: ${DS_BREAKPOINTS[bp]}px)`;
    },
    down(bp: keyof typeof DS_BREAKPOINTS) {
      return `@media (max-width: ${DS_BREAKPOINTS[bp] - 1}px)`;
    },
    between(
      min: keyof typeof DS_BREAKPOINTS,
      max: keyof typeof DS_BREAKPOINTS
    ) {
      return `@media (min-width: ${DS_BREAKPOINTS[min]}px) and (max-width: ${
        DS_BREAKPOINTS[max] - 1
      }px)`;
    },
  },
  animation: {
    duration: {
      short: '150ms',
      base: '200ms',
      long: '400ms',
    },
    easing: {
      standard: 'ease-in-out',
      accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
      decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
      elastic: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    },
  },
} as const;

// ===== Fonts =====
export const DS_FONTS = {
  'heading-1': {
    fontSize: '32px',
    lineHeight: '40px',
    fontWeight: '500',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 32px;
      line-height: 40px;
      font-weight: 500;
    `,
  },
  'heading-2': {
    fontSize: '24px',
    lineHeight: '32px',
    fontWeight: '500',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 24px;
      line-height: 32px;
      font-weight: 500;
    `,
  },
  'heading-3': {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: '500',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 16px;
      line-height: 24px;
      font-weight: 500;
    `,
  },
  'heading-4': {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '500',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
    `,
  },
  'heading-5': {
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: '500',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 13px;
      line-height: 16px;
      font-weight: 500;
    `,
  },
  'body-16': {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 16px;
      line-height: 24px;
      font-weight: 400;
    `,
  },
  'body-14': {
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
    `,
  },
  'caption-13': {
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 13px;
      line-height: 16px;
      font-weight: 400;
    `,
  },
  'numbers-13': {
    fontSize: '13px',
    lineHeight: '16px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto Mono';
      font-size: 13px;
      line-height: 16px;
      font-weight: 400;
    `,
  },
  'caption-11': {
    fontSize: '11px',
    lineHeight: '12px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 11px;
      line-height: 12px;
      font-weight: 400;
    `,
  },
  'button-16': {
    fontSize: '16px',
    lineHeight: '16px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 16px;
      line-height: 16px;
      font-weight: 400;
    `,
  },
  'button-14': {
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      line-height: 16px;
      font-weight: 400;
    `,
  },
  'button-12': {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: '400',
    css: css`
      font-family: 'Roboto', sans-serif;
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
    `,
  },
};

// ===== Theme =====
export const v4Theme = {
  colors: DSLightColors,
  spacings: DS_SPACINGS,
  shadows: DS_SHADOWS,
  tokens: DS_TOKENS,
  radiuses: DS_RADIUSES,
  fonts: DS_FONTS,
  mode: 'light' as const,
};

export type V4Theme = typeof v4Theme;

// ===== V4ThemeProvider =====
export const V4ThemeProvider = ({ children }: { children: ReactNode }) => (
  <ThemeProvider theme={v4Theme}>
    <GlobalStyles />
    {children}
  </ThemeProvider>
);

// ===== useV4Theme =====
export const useV4Theme = (): V4Theme => useTheme() as unknown as V4Theme;

// ===== getDSColor =====
type DSColorsKeysInternal = {
  [K in keyof typeof DSLightColors]: typeof DSLightColors[K] extends Record<
    string | number,
    string
  >
    ? `${K & string}.${keyof typeof DSLightColors[K] & (string | number)}`
    : K & string;
}[keyof typeof DSLightColors];

export const getDSColor = (
  theme: V4Theme,
  colorKey: DSColorsKeysInternal = 'gray.800'
): string => {
  const [palette, shade] = colorKey.split('.') as [
    keyof typeof theme['colors'],
    string
  ];
  const colorGroup = theme.colors[palette];

  return typeof colorGroup === 'string'
    ? colorGroup
    : (colorGroup as Record<string, string>)?.[shade];
};

export const hexToRGB = (hex: string, opacity?: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (opacity !== undefined) {
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return `rgb(${r}, ${g}, ${b})`;
};
